import React, { memo, useEffect, useState, type FC } from 'react';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

const DURATION_MS = 1200;

export type SplashLoadingIndicatorProps = {
  /**
   * Determinate progress in the range `0..1`. When provided, the bar fills proportionally
   * and a `"Resource updating ... XX%"` label is shown above the track.
   * When omitted, the indicator renders an infinite indeterminate animation.
   */
  progress?: number;
};

const clampProgress = (value: number) => Math.max(0, Math.min(1, value));

/**
 * Splash loading indicator.
 * - Indeterminate (no `progress` prop): segment slides across the track on a loop.
 * - Determinate (`progress` prop set): bar fills to the given ratio and shows percent text.
 */
export const SplashLoadingIndicator: FC<SplashLoadingIndicatorProps> = memo(({ progress }) => {
  const { scale } = useScaling();
  const styles = useStyles();
  const SEGMENT_WIDTH = scale(80);
  const TRACK_HEIGHT = scale(8);
  const isDeterminate = typeof progress === 'number';

  const segmentLeft = useSharedValue(-SEGMENT_WIDTH);
  const fillRatio = useSharedValue(isDeterminate ? clampProgress(progress!) : 0);
  const [trackWidth, setTrackWidth] = useState(0);
  // Mirror the animated fill ratio so the percent label is always in sync with the bar.
  // const initialPercent = isDeterminate ? Math.round(clampProgress(progress!) * 100) : 0;
  // const [displayedPercent, setDisplayedPercent] = useState(initialPercent);

  const onTrackLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0) setTrackWidth(w);
  };

  useEffect(() => {
    if (isDeterminate || trackWidth <= 0) return;
    const startLeft = -SEGMENT_WIDTH;
    const endLeft = trackWidth;
    segmentLeft.value = startLeft;
    segmentLeft.value = withRepeat(
      withSequence(
        withTiming(endLeft, { duration: DURATION_MS }),
        withTiming(startLeft, { duration: 0 }),
      ),
      -1,
    );
  }, [trackWidth, segmentLeft, SEGMENT_WIDTH, isDeterminate]);

  useEffect(() => {
    if (!isDeterminate) return;
    fillRatio.value = withTiming(clampProgress(progress!), { duration: 250 });
  }, [progress, isDeterminate, fillRatio]);

  // Drive the label off the same shared value as the bar fill so the text counts up
  // smoothly alongside the animation instead of jumping ahead to the target prop value.
  // useAnimatedReaction(
  //   () => fillRatio.value,
  //   (current, previous) => {
  //     'worklet';
  //     // Inline clamp (UI-thread context, can't call non-worklet helpers).
  //     const clamped = current < 0 ? 0 : current > 1 ? 1 : current;
  //     const next = Math.round(clamped * 100);
  //     const prevClamped = previous == null ? null : previous < 0 ? 0 : previous > 1 ? 1 : previous;
  //     const prev = prevClamped == null ? -1 : Math.round(prevClamped * 100);
  //     if (next !== prev) runOnJS(setDisplayedPercent)(next);
  //   },
  // );

  const segmentAnimatedStyle = useAnimatedStyle(() => ({
    left: segmentLeft.value,
  }));

  // const fillAnimatedStyle = useAnimatedStyle(() => ({
  //   width: `${fillRatio.value * 100}%`,
  // }));

  const percentLabel = isDeterminate ? Math.round(clampProgress(progress!) * 100) : 0;

  return (
    <Stack width="100%">
      {isDeterminate && (
        <Text variant="Font Inter/Subtitle/Medium 12px" style={styles.label}>
          {`Resources Updating... ${percentLabel}%`}
        </Text>
      )}
      <Stack
        direction="row"
        width="100%"
        height={TRACK_HEIGHT}
        backgroundColor={colors['Blue 2']}
        style={styles.track}
        onLayout={onTrackLayout}>
        {isDeterminate ? null : ( // <Animated.View style={[styles.fill, fillAnimatedStyle]} />
          <View style={styles.segmentWrap}>
            <Animated.View style={[styles.segment, segmentAnimatedStyle]} />
          </View>
        )}
      </Stack>
    </Stack>
  );
});

const useStyles = createStyles(({ scale }) => ({
  track: {
    overflow: 'hidden',
  },
  segmentWrap: {
    ...StyleSheet.absoluteFill,
    left: 0,
    right: 0,
  },
  segment: {
    position: 'absolute',
    width: scale(80),
    height: scale(8),
    backgroundColor: colors.Blue,
  },
  fill: {
    height: scale(8),
    backgroundColor: colors.Blue,
  },
  label: {
    color: colors.Blue,
    textAlign: 'center',
  },
}));
